import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { getDisplayName, Role } from '../utils/models/tool.models';
import { CapabilityStore } from '../services/capability.store';
import { UserService } from '../services/api-services/user.service';
import {
  ButtonDirective,
  ExpandableItemComponent,
  ExpandableItemTriggerDirective,
} from '@innersource/common-ui-components';

const SIDEBAR_RUNS_PER_RESOURCE = 5;

type SidebarRun = { id: number };

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, ButtonDirective, ExpandableItemComponent, ExpandableItemTriggerDirective],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  /** Forward to the template. */
  readonly getDisplayName = getDisplayName;

  /** Inject services. */
  private readonly capabilityStore = inject(CapabilityStore);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  /** State. */
  readonly tools = this.capabilityStore.tools;
  readonly toolchains = this.capabilityStore.toolchains;
  readonly historiesReady = this.capabilityStore.areHistoriesReady;
  readonly user = this.userService.user;
  private readonly routeUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );
  readonly activeToolId = computed(() => this.resourceId('tools'));
  readonly activeToolchainId = computed(() => this.resourceId('toolchains'));
  readonly activeToolRunId = computed(() => this.runId('tools'));
  readonly activeToolchainRunId = computed(() => this.runId('toolchains'));
  private readonly expandedItems = signal<ReadonlySet<string>>(new Set());

  isAdmin = computed(() => this.user()?.role === Role.Admin);

  constructor() {
    // Home resets expanded groups; tool and toolchain groups remain independently expandable.
    effect(() => {
      if (this.isDashboardRoute()) {
        untracked(() => this.expandedItems.set(new Set()));
        return;
      }

      const itemId = this.activeItemId();

      if (itemId) {
        untracked(() => this.expandedItems.update(items => new Set(items).add(itemId)));
      }
    });
  }

  isDashboardActive(): boolean {
    return this.routeUrl() === '/dashboard';
  }

  isDashboardRoute(): boolean {
    return this.routeUrl().startsWith('/dashboard');
  }

  isAdminActive(): boolean {
    return this.routeUrl().startsWith('/admin');
  }

  isToolActive(toolId: number): boolean {
    return this.activeToolId() === toolId;
  }

  isToolOverviewActive(toolId: number): boolean {
    return this.routeUrl() === `/tools/${toolId}`;
  }

  isToolchainActive(toolchainId: number): boolean {
    return this.activeToolchainId() === toolchainId;
  }

  isToolchainOverviewActive(toolchainId: number): boolean {
    return this.routeUrl() === `/toolchains/${toolchainId}`;
  }

  isToolRunActive(toolId: number, runId: number): boolean {
    return this.routeUrl() === `/tools/${toolId}/results/${runId}`;
  }

  isToolNewRunActive(toolId: number): boolean {
    return this.routeUrl() === `/tools/${toolId}/run`;
  }

  isToolHistoryLoading(toolId: number): boolean {
    return this.capabilityStore.jobHistory(toolId).loading();
  }

  isToolchainRunActive(toolchainId: number, runId: number): boolean {
    return this.routeUrl() === `/toolchains/${toolchainId}/results/${runId}`;
  }

  isToolchainNewRunActive(toolchainId: number): boolean {
    return this.routeUrl() === `/toolchains/${toolchainId}/run`;
  }

  isToolchainHistoryLoading(toolchainId: number): boolean {
    return this.capabilityStore.toolchainJobHistory(toolchainId).loading();
  }

  isExpanded(itemId: string): boolean {
    return this.expandedItems().has(itemId);
  }

  setExpanded(itemId: string, expanded: boolean): void {
    this.expandedItems.update(items => {
      const next = new Set(items);
      if (expanded) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }

  runsForTool(toolId: number): SidebarRun[] {
    const runs = this.capabilityStore
      .jobHistory(toolId)
      .items()
      .slice(0, SIDEBAR_RUNS_PER_RESOURCE)
      .map(run => ({ id: run.toolJobId }));
    const activeRunId = this.isToolActive(toolId) ? this.activeToolRunId() : undefined;

    // Keep an older currently open run reachable below the five recent entries.
    return activeRunId && !runs.some(run => run.id === activeRunId)
      ? [...runs, { id: activeRunId }]
      : runs;
  }

  runsForToolchain(toolchainId: number): SidebarRun[] {
    const runs = this.capabilityStore
      .toolchainJobHistory(toolchainId)
      .items()
      .slice(0, SIDEBAR_RUNS_PER_RESOURCE)
      .map(run => ({ id: run.toolchainJobId }));
    const activeRunId = this.isToolchainActive(toolchainId)
      ? this.activeToolchainRunId()
      : undefined;

    // Keep an older currently open run reachable below the five recent entries.
    return activeRunId && !runs.some(run => run.id === activeRunId)
      ? [...runs, { id: activeRunId }]
      : runs;
  }

  private resourceId(resourceType: 'tools' | 'toolchains'): number | undefined {
    const match = this.routeUrl().match(new RegExp(`^/${resourceType}/(\\d+)`));
    return match ? Number(match[1]) : undefined;
  }

  private runId(resourceType: 'tools' | 'toolchains'): number | undefined {
    const match = this.routeUrl().match(new RegExp(`^/${resourceType}/\\d+/results/(\\d+)`));
    return match ? Number(match[1]) : undefined;
  }

  private activeItemId(): string | undefined {
    const toolId = this.activeToolId();
    if (toolId !== undefined) return `tool-${toolId}`;

    const toolchainId = this.activeToolchainId();
    if (toolchainId === undefined) return undefined;
    return `toolchain-${toolchainId}`;
  }
}
